import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
	UntypedFormBuilder,
	UntypedFormGroup,
	Validators,
} from '@angular/forms';
import {
	DateAdapter,
	MAT_DATE_FORMATS,
	MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
	MAT_MOMENT_DATE_ADAPTER_OPTIONS,
	MomentDateAdapter,
} from '@angular/material-moment-adapter';

import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Store } from '@ngrx/store';
import moment from 'moment';

import { BeneficiaryInfoDetailsDto } from '../../../../../modules/beneficiaries/dtos/beneficiary-info-details';
import { WalletCreateDto } from '../../../../../modules/wallets/dtos/wallet-create';
import { WalletsService } from '../../../../../modules/wallets/wallets.service';
import { SnackBarService } from '../../../../../shared/components/snack-bar/snack-bar.service';
import { PartnerType } from '../../../../../shared/enums/partner-type.enum';
import { WalletType } from '../../../../../shared/enums/wallet-type.enum';
import { PT_BR_DATE_FORMATS } from '../../../../../utils/date-formats.utils';
import { BeneficiaryInfoResponse } from '../../../models/beneficiaries.model';
import { fetchBeneficiaryAllBenefitsStart } from '../../../state/organizations.actions';
import { selectBeneficiaryAllBenefits } from '../../../state/organizations.selector';

@Component({
	providers: [
		{ provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
		{
			deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
			provide: DateAdapter,
			useClass: MomentDateAdapter,
		},
		{ provide: MAT_DATE_FORMATS, useValue: PT_BR_DATE_FORMATS },
	],
	selector: 'app-add-congener-benefit-modal',
	templateUrl: './add-congener-benefit-modal.component.html',
})
export class AddCongenerBenefitModalComponent implements OnInit, OnDestroy {
	availableBenefits: BeneficiaryInfoDetailsDto[] = [];
	dentalBenefits: BeneficiaryInfoDetailsDto[] = [];
	form: UntypedFormGroup;

	healthBenefits: BeneficiaryInfoDetailsDto[] = [];
	inputId = String(Math.random());
	isLoading = true;

	partnerTypeEnum = PartnerType;

	private onSubmitSubscription$: Subscription = new Subscription();
	private readonly unsubscribe$ = new Subject<void>();

	constructor(
		private readonly fb: UntypedFormBuilder,
		private readonly store: Store,
		private readonly walletsService: WalletsService,
		private readonly snackbarService: SnackBarService,
		public dialogRef: MatDialogRef<AddCongenerBenefitModalComponent>,
		@Inject(MAT_DIALOG_DATA)
		public data: {
			beneficiaryId: string;
			organizationId: string;
			positionId: string;
		},
	) {
		this.form = this.fb.group({
			benefitId: [{ disabled: true, value: null }, Validators.required],
			planType: [null, Validators.required],
			startDate: [{ disabled: true, value: null }, Validators.required],
			walletNumber: [{ disabled: true, value: null }],
		});
	}

	closeModal(): void {
		this.dialogRef.close(false);
	}

	fetchAvailableBenefits(): void {
		this.isLoading = true;
		this.store.dispatch(
			fetchBeneficiaryAllBenefitsStart({
				id: this.data.organizationId,
				positionId: this.data.positionId,
			}),
		);

		this.store
			.select(selectBeneficiaryAllBenefits)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((response: BeneficiaryInfoResponse | null) => {
				if (response) {
					this.healthBenefits = response.healths;
					this.dentalBenefits = response.teeths;
				}
				this.isLoading = false;
			});
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
		this.onSubmitSubscription$.unsubscribe();
	}

	ngOnInit(): void {
		this.fetchAvailableBenefits();
		this.setupPlanTypeListener();
	}

	onContinue(): void {
		if (this.form.invalid) {
			this.updateValidation();

			return;
		}

		this.isLoading = true;
		const formValue = this.form.value;

		const dto: WalletCreateDto = {
			beneficiaryId: this.data.beneficiaryId,
			benefitId: formValue.benefitId,
			isHealth: formValue.planType === PartnerType.HEALTH,
			isNotApplicable: false,
			isTeeth: formValue.planType === PartnerType.TEETH,
			type: WalletType.CONGENER,
			updatedInInsuranceAt: formValue.startDate
				? moment(formValue.startDate).format('YYYY-MM-DD hh:mm:ssZZ')
				: null,
			walletNumber: formValue.walletNumber,
		};

		this.onSubmitSubscription$ = this.walletsService
			.createWallet({ dto })
			.subscribe({
				error: (error) => {
					this.snackbarService.showSnack(
						error.error?.message || 'Erro ao adicionar plano congênere.',
						'error',
					);
					this.isLoading = false;
				},
				next: (response) => {
					this.snackbarService.showSnack(
						response?.message || 'Plano congênere adicionado com sucesso.',
						'success',
					);
					this.dialogRef.close(true);
					this.isLoading = false;
				},
			});
	}

	setupPlanTypeListener(): void {
		this.form
			.get('planType')
			?.valueChanges.pipe(takeUntil(this.unsubscribe$))
			.subscribe((type: PartnerType | null) => {
				this.availableBenefits = [];
				this.form.get('benefitId')?.reset({ disabled: true, value: null });
				this.form.get('walletNumber')?.reset({ disabled: true, value: null });
				this.form.get('startDate')?.reset({ disabled: true, value: null });

				if (type) {
					this.availableBenefits =
						type === PartnerType.HEALTH
							? this.healthBenefits
							: this.dentalBenefits;
					this.form.get('benefitId')?.enable();
					this.form.get('walletNumber')?.enable();
					this.form.get('startDate')?.enable();
				}
			});
	}

	updateValidation(): void {
		for (const control in this.form.controls) {
			this.form.get(control)!.markAsTouched();
			this.form.get(control)!.updateValueAndValidity({ onlySelf: true });
		}
	}
}
